/**
 * `<AmbientPill/>` — the shared chrome for the ambient pill / ticker
 * sections of the persistent right panel (DUP4, cleanup-findings §2 C2).
 *
 * **Why this exists.** A dozen panel sections (`<ForecastPillSection/>`,
 * `<HoursPillSection/>`, `<RetrospectivePillSection/>`,
 * `<SubmissionProgressSection/>`, `<LiveMoodSection/>`,
 * `<RebalanceFlagSection/>`, `<TeamAuditTickerSection/>`,
 * `<MeAuditTickerSection/>`, `<WorkflowTriggerSection/>`,
 * `<ValidationSummarySection/>`) all hand-rolled the same section/header/
 * skeleton scaffold: a calm bordered (or flat) `<section>` → a `<header>`
 * carrying an icon + title on the left and a "See detail →" deep link on
 * the right → a loading `<Skeleton>` → the success body. This component
 * captures that scaffold once so each section is reduced to its own fetch
 * + self-hide branch + the success body.
 *
 * **Behaviour-preserving.** The rendered chrome is byte-identical to the
 * hand-rolled version each section shipped — the props expose every axis
 * the sections actually varied on (the container `variant`, the header
 * icon + its size/tone, the title weight/composition, the deep-link
 * label + whether it carries a trailing arrow glyph, plus passthrough
 * for any extra `<section>` data-attributes). Each section keeps its own
 * testid, its own VERBATIM copy, and its own self-hide-vs-error
 * distinction (#781) — `AmbientPill` only renders once a section has
 * already decided it is in a *success* state.
 *
 * **Error handling is NOT here.** The expected-empty-vs-unexpected-error
 * decision (#781) stays inside each section, which composes the existing
 * `<AmbientErrorNotice/>` + `isUnexpectedApiError` (from
 * `./AmbientErrorNotice`) before it ever reaches `AmbientPill`. This
 * component deliberately does not duplicate that logic.
 *
 * **Brand discipline** (`feedback_brand_pillars.md` +
 * `feedback_ui_section_focal_component.md`): the pill is supporting
 * chrome, never a focal element — the focal element lives in the
 * section's `children` body. The icon + deep link stay calm.
 */
import { ArrowRight, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Skeleton } from "./ui";

/**
 * Container chrome variant.
 * - `"card"` — the bordered, padded pill (`<ForecastPillSection/>`,
 *   `<HoursPillSection/>`): a `rounded-md border border-border/60
 *   bg-bg/40 p-3` box.
 * - `"flat"` — the borderless ticker row (`<LiveMoodSection/>`,
 *   `<SubmissionProgressSection/>`, …): a `px-1 py-2` row that leans on
 *   the panel divider for separation instead of its own border.
 */
export type AmbientPillVariant = "card" | "flat";

const CONTAINER_CLASS: Record<AmbientPillVariant, string> = {
  card: "flex flex-col gap-2 rounded-md border border-border/60 bg-bg/40 p-3",
  flat: "flex flex-col gap-2 px-1 py-2",
};

export interface AmbientPillProps {
  /**
   * Header icon (a lucide-react component, rendered `aria-hidden`).
   * Omit for the iconless sections (`<SubmissionProgressSection/>`,
   * `<ValidationSummarySection/>`) — the title then renders as a bare
   * `text-xs font-semibold` label, byte-identical to what they shipped.
   */
  icon?: LucideIcon;
  /** Header title text. */
  title: string;
  /** Deep-link target for the trailing detail link. */
  detailTo: string;
  /** Deep-link label. Defaults to the canonical `See detail →`. */
  detailLabel?: ReactNode;
  /** Test hook on the wrapping `<section>`. */
  testId: string;
  /** True while the section's query is loading — renders the skeleton. */
  isLoading: boolean;
  /** The success body — rendered when `isLoading` is false. */
  children: ReactNode;

  // ─── Chrome-variation knobs (each section preserved its own) ───────

  /** Container chrome. Defaults to the bordered `"card"` pill. */
  variant?: AmbientPillVariant;
  /** Accessible label on the `<section>`. Defaults to `title`. */
  ariaLabel?: string;
  /** Tailwind sizing for the header icon. Defaults to `h-4 w-4`. */
  iconClassName?: string;
  /**
   * Inline content the section wants between the title and the deep
   * link (e.g. the workflow unack-count pill). Rendered inside the
   * same `gap-1.5` cluster as the icon + title.
   */
  headerAccessory?: ReactNode;
  /**
   * Tailwind classes for the deep-link anchor. Defaults to the
   * canonical card-pill text-link styling.
   */
  detailLinkClassName?: string;
  /** Test hook on the deep-link anchor. */
  detailTestId?: string;
  /**
   * Render a trailing `→` arrow glyph after the detail label. The
   * card pills bake the arrow into their copy (`See detail →`); the
   * flat ticker rows render a separate `<ArrowRight/>` icon. Defaults
   * to false (arrow lives in `detailLabel`).
   */
  detailArrow?: boolean;
  /** Tailwind classes for the loading `<Skeleton>`. */
  skeletonClassName?: string;
  /** Test hook on the loading `<Skeleton>`. */
  skeletonTestId?: string;
  /** Extra data-* / attributes spread onto the `<section>` element. */
  sectionProps?: Record<string, string | number | undefined>;
}

export function AmbientPill({
  icon: Icon,
  title,
  detailTo,
  detailLabel = "See detail →",
  testId,
  isLoading,
  children,
  variant = "card",
  ariaLabel,
  iconClassName = "h-4 w-4 text-accent-fg",
  headerAccessory,
  detailLinkClassName = "text-[11px] text-fg-muted hover:text-fg",
  detailTestId,
  detailArrow = false,
  skeletonClassName,
  skeletonTestId,
  sectionProps,
}: AmbientPillProps) {
  return (
    <section
      data-testid={testId}
      className={CONTAINER_CLASS[variant]}
      aria-label={ariaLabel ?? title}
      {...sectionProps}
    >
      <header className="flex items-center justify-between gap-2">
        {Icon ? (
          <div className="flex items-center gap-1.5">
            <Icon className={iconClassName} aria-hidden="true" />
            <span className="text-xs font-semibold text-fg">{title}</span>
            {headerAccessory}
          </div>
        ) : (
          <div className="text-xs font-semibold text-fg">{title}</div>
        )}
        <Link to={detailTo} className={detailLinkClassName} data-testid={detailTestId}>
          {detailLabel}
          {detailArrow ? <ArrowRight className="h-3 w-3" aria-hidden="true" /> : null}
        </Link>
      </header>

      {isLoading ? (
        <Skeleton className={skeletonClassName} data-testid={skeletonTestId} />
      ) : (
        children
      )}
    </section>
  );
}
